import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, Loader2, Pencil, Plus, Save, Search, X } from 'lucide-react';
import pestModelService from '../../services/pestModels';
import { errorMessage } from '../../utils/apiResponse';
import type {
  PestModel, PestChemicalsResult, PestChemical, PestTargetLink, RegisteredPestOption, DiseaseActivity,
} from '../../types/pest';

type ActivityFilter = 'all' | 'protectant' | 'curative';

const ACTIVITY_LABEL: Record<DiseaseActivity, string> = {
  protectant: 'Protectant',
  curative: 'Curative',
  both: 'Protectant + curative',
};
const ACTIVITY_CLS: Record<DiseaseActivity, string> = {
  protectant: 'bg-sky-50 text-sky-800 border-sky-200',
  curative: 'bg-violet-50 text-violet-800 border-violet-200',
  both: 'bg-teal-50 text-teal-800 border-teal-200',
};

/**
 * The products registered against a pest, on its host crops. What makes a
 * registration "for" this pest is the list of target-pest names it is linked
 * to — labels spell one disease several ways — so that list sits on top and
 * a spray admin can correct it.
 */
export default function PestChemicalsPanel({ pest, canManage }: { pest: PestModel; canManage: boolean }) {
  const [data, setData] = useState<PestChemicalsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [otherCrops, setOtherCrops] = useState(false);
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try { setData(await pestModelService.getChemicals(pest.id, otherCrops)); setError(null); }
    catch (e) { setError(errorMessage(e)); }
  }, [pest.id, otherCrops]);
  useEffect(() => { load(); }, [load]);

  const isDisease = pest.kind === 'disease';

  const shown = useMemo(() => {
    const list = data?.chemicals ?? [];
    if (filter === 'all') return list;
    return list.filter((c) => {
      const a = c.protection?.activity;
      return a === filter || a === 'both';
    });
  }, [data, filter]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return <div className="flex items-center gap-2 text-sm text-neutral-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading products…</div>;

  return (
    <div className="space-y-4">
      {/* What it is called on labels */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-neutral-800">Called on labels</h3>
          {canManage && !editing && (
            <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-xs text-primary-700 hover:underline">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          )}
        </div>
        {editing ? (
          <TargetPestEditor
            pest={pest}
            initial={data.target_pests}
            onCancel={() => setEditing(false)}
            onSaved={async () => { setEditing(false); await load(); }}
          />
        ) : data.target_pests.length ? (
          <div className="flex flex-wrap gap-1.5">
            {data.target_pests.map((t) => (
              <span key={t.pest_key} className="px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 text-xs">{t.label}</span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-neutral-500">
            Not linked to any registration target pest yet, so no products can be listed
            {isDisease ? ' and every fungicide sprayed is counted as protection against it' : ''}.
            {canManage ? ' Use Edit to pick the names labels use for it.' : ''}
          </p>
        )}
      </div>

      {data.target_pests.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {isDisease && (['all', 'protectant', 'curative'] as ActivityFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border ${filter === f ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-neutral-600 border-neutral-300 hover:bg-neutral-50'}`}
              >
                {f === 'all' ? 'All' : f === 'protectant' ? 'Protectants' : 'Curatives'}
              </button>
            ))}
            {data.host_limited && (
              <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-neutral-600">
                <input type="checkbox" checked={otherCrops} onChange={(e) => setOtherCrops(e.target.checked)} />
                Other crops too{!otherCrops && data.other_crop_product_count ? ` (${data.other_crop_product_count})` : ''}
              </label>
            )}
          </div>

          {data.host_limited && data.host_situation_count === 0 && !otherCrops && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              None of this pest&apos;s host crops are linked to a spray situation, so no registration can be matched to them.
              Link the crop types under Cultivar admin → Crop types, or tick “Other crops too”.
            </p>
          )}

          {!shown.length ? (
            <p className="text-sm text-neutral-500 py-2">No {filter === 'all' ? '' : `${filter} `}products registered against it{data.host_limited && !otherCrops ? ' on its host crops' : ''}.</p>
          ) : (
            <ul className="space-y-2">
              {shown.map((c) => <ChemicalRow key={c.chemical_id} c={c} />)}
            </ul>
          )}
          <p className="text-xs text-neutral-400">
            Protectant and curative are read from the product&apos;s FRAC group unless set on the chemical. Rates are the label&apos;s — always check the label before spraying.
          </p>
        </>
      )}
    </div>
  );
}

function ChemicalRow({ c }: { c: PestChemical }) {
  const p = c.protection;
  return (
    <li className="border border-neutral-200 rounded-lg px-3 py-2.5 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <FlaskConical className="h-4 w-4 text-neutral-400 shrink-0" />
        <Link to={`/spray/chemicals/${c.chemical_id}`} className="font-medium text-sm text-primary-700 hover:underline break-words min-w-0">
          {c.product_name}
        </Link>
        {c.mode_of_action_group && (
          <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">Group {c.mode_of_action_group}</span>
        )}
        {p?.activity && (
          <span className={`text-[11px] px-1.5 py-0.5 rounded border ${ACTIVITY_CLS[p.activity]}`}>{ACTIVITY_LABEL[p.activity]}</span>
        )}
      </div>
      {p?.activity && (
        <div className="text-xs text-neutral-500">
          {p.washoff_mm != null && <>Washes off at {p.washoff_mm} mm{p.source.washoff === 'default' ? ' (default)' : ''}</>}
          {p.washoff_mm != null && p.kickback_hours != null && ' · '}
          {p.kickback_hours != null && <>{p.kickback_hours} h kickback{p.source.kickback === 'group' ? ' (group default)' : ''}</>}
        </div>
      )}
      <ul className="text-xs text-neutral-600 space-y-0.5">
        {c.registrations.map((r) => (
          <li key={r.id} className="break-words">
            <span className="font-medium text-neutral-700">{r.situation_name || 'Any situation'}</span>
            {r.target_pest ? ` — ${r.target_pest}` : ''}
            {' · '}{rateText(r.min_rate_per_ha, r.max_rate_per_ha, r.rate_unit, r.rate_status)}
            {r.whp_days != null && ` · WHP ${r.whp_days} d`}
            {r.max_applications_per_season != null && ` · max ${r.max_applications_per_season}/season`}
          </li>
        ))}
      </ul>
    </li>
  );
}

function rateText(min: unknown, max: unknown, unit: string | null, status: string | null): string {
  const lo = min != null && min !== '' ? Number(min) : null;
  const hi = max != null && max !== '' ? Number(max) : null;
  if (lo == null && hi == null) return 'rate not recorded — see label';
  const range = lo != null && hi != null && lo !== hi ? `${lo}–${hi}` : String(hi ?? lo);
  return `${range} ${unit || ''}${status === 'proposed' ? ' (read from label, unconfirmed)' : ''}`.trim();
}

function TargetPestEditor({ pest, initial, onCancel, onSaved }: {
  pest: PestModel; initial: PestTargetLink[]; onCancel: () => void; onSaved: () => void;
}) {
  const [links, setLinks] = useState<Array<Pick<PestTargetLink, 'label' | 'apvma_pestcode'>>>(initial.map((t) => ({ label: t.label, apvma_pestcode: t.apvma_pestcode })));
  const [q, setQ] = useState(initial.length ? '' : (pest.common_name || pest.name).split(/[/(,]/)[0].trim());
  const [results, setResults] = useState<RegisteredPestOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    let live = true;
    setSearching(true);
    const t = setTimeout(() => {
      pestModelService.searchRegisteredPests(term)
        .then((r) => { if (live) setResults(r); })
        .catch(() => { if (live) setResults([]); })
        .finally(() => { if (live) setSearching(false); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);

  const keyOf = (label: string) => label.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  const has = (label: string) => links.some((l) => keyOf(l.label) === keyOf(label));

  const save = async () => {
    setSaving(true);
    setError(null);
    try { await pestModelService.setTargetPests(pest.id, links); onSaved(); }
    catch (e) { setError(errorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-neutral-200 rounded-lg p-3 space-y-3">
      <div className="flex flex-wrap gap-1.5 min-h-[1.75rem]">
        {links.length === 0 && <span className="text-xs text-neutral-400">None yet — search below and add the names labels use.</span>}
        {links.map((l) => (
          <button
            key={keyOf(l.label)}
            type="button"
            onClick={() => setLinks(links.filter((x) => x !== l))}
            className="inline-flex items-center gap-1 max-w-full rounded-full bg-primary-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-700"
            aria-label={`Remove ${l.label}`}
          >
            <span className="break-words text-left">{l.label}</span><X className="h-3 w-3 shrink-0" />
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-neutral-300">
        <div className="flex items-center gap-2 border-b border-neutral-100 px-3 py-2">
          <Search className="h-4 w-4 shrink-0 text-neutral-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search registered target pests…"
            aria-label="Search registered target pests"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
          {searching && <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />}
        </div>
        <ul className="max-h-56 overflow-y-auto divide-y divide-neutral-100">
          {results.map((r) => (
            <li key={r.pest_key}>
              <button
                type="button"
                disabled={has(r.label)}
                onClick={() => setLinks([...links, { label: r.label, apvma_pestcode: r.apvma_pestcode }])}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-neutral-50 disabled:opacity-40"
              >
                <Plus className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
                <span className="flex-1 min-w-0 break-words">{r.label}</span>
                <span className="text-xs text-neutral-400 shrink-0">{r.product_count} product{r.product_count === 1 ? '' : 's'}</span>
              </button>
            </li>
          ))}
          {!searching && q.trim().length >= 2 && !results.length && <li className="px-3 py-2 text-xs text-neutral-400">No registration names that.</li>}
        </ul>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
        <button onClick={onCancel} className="text-sm px-3 py-1.5 rounded-lg bg-neutral-100 hover:bg-neutral-200">Cancel</button>
      </div>
    </div>
  );
}
